/**
 * Wiederkehrende Buchungen – Analyse & Uebersicht
 *
 * Analysiert alle Bank-Statement-Zeilen der vergangenen Jahre,
 * erkennt wiederkehrende Muster (gleicher Empfaenger, aehnlicher Betrag,
 * regelmaessiges Intervall) und stellt sie uebersichtlich dar.
 *
 * Der User kann aus erkannten Gruppen direkt Buchungsmuster (Booking Patterns)
 * erstellen, um kuenftige Buchungen zu automatisieren.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { apiClient } from '@/lib/api.ts'
import { useTenantStore } from '@/stores/tenants.ts'
import {
  RefreshCw, TrendingUp, TrendingDown, CalendarClock,
  Save, Search, BarChart3, Repeat, ChevronDown, ChevronRight,
} from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterChip,
  Input,
  LoadingState,
  PageHeader,
  Select,
  Spinner,
  useToast,
  type ColumnDef,
  type Row,
} from '@/components/ui'
import type { BadgeProps } from '@/components/ui'

// ─── Types ──────────────────────────────────────────────────────────────────

type RecurrenceFrequency =
  | 'weekly' | 'biweekly' | 'monthly' | 'quarterly'
  | 'semiannual' | 'annual' | 'irregular'

interface RecurringOccurrence {
  tenantId: string
  statementAbsEntry: number
  lineNum: number
  postingDate: string
  amount: number
  counterpartyName: string
  details: string
  bpCode: string | null
  glAccount: string | null
  isBooked: boolean
}

interface RecurringGroup {
  groupKey: string
  counterpartyName: string
  counterpartyNormalized: string
  direction: 'debit' | 'credit'
  typicalAmount: number
  category: 'expense' | 'income' | 'internal_transfer'
  amountMin: number
  amountMax: number
  amountStdev: number
  count: number
  firstDate: string
  lastDate: string
  nextExpectedDate: string | null
  medianIntervalDays: number
  intervalStdevDays: number
  frequency: RecurrenceFrequency
  confidence: number
  estimatedAnnualAmount: number
  unbookedCount: number
  sampleDetails: string
  occurrences: RecurringOccurrence[]
}

interface AnalysisDebug {
  totalRawRows: number
  droppedNoDate: number
  droppedDateOutOfRange: number
  droppedZeroAmount: number
  rowsAfterFilter: number
  rowsWithCounterparty: number
  rowsWithoutCounterparty: number
  bucketsFormed: number
  bucketsWithEnoughOccurrences: number
  groupsBeforeConfidenceFilter: number
  groupsAfterConfidenceFilter: number
}

interface RecurringAnalysis {
  tenantId: string
  windowFromDate: string
  windowToDate: string
  statementsScanned: number
  rowsScanned: number
  groups: RecurringGroup[]
  errors: string[]
  generatedAt: string
  cached: boolean
  debug?: AnalysisDebug
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const freqLabels: Record<RecurrenceFrequency, string> = {
  weekly: 'Wöchentlich',
  biweekly: 'Zweiwöchentlich',
  monthly: 'Monatlich',
  quarterly: 'Quartalsweise',
  semiannual: 'Halbjährlich',
  annual: 'Jährlich',
  irregular: 'Unregelmäßig',
}

function formatEur(n: number): string {
  return n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
}

function formatDate(iso: string): string {
  if (!iso) return '–'
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function confidenceVariant(score: number): NonNullable<BadgeProps['variant']> {
  if (score >= 80) return 'success'
  if (score >= 60) return 'warning'
  return 'danger'
}

function DirectionIcon({ direction }: { direction: 'debit' | 'credit' }) {
  return direction === 'debit'
    ? <TrendingDown className="w-4 h-4 text-red-500 inline mr-1" />
    : <TrendingUp className="w-4 h-4 text-green-500 inline mr-1" />
}

const MONTHS_OPTIONS = [
  { value: '6', label: 'Letzte 6 Monate' },
  { value: '12', label: 'Letztes Jahr' },
  { value: '24', label: 'Letzte 2 Jahre' },
  { value: '36', label: 'Letzte 3 Jahre' },
  { value: '60', label: 'Letzte 5 Jahre' },
]

const FREQ_OPTIONS = [
  { value: 'all', label: 'Alle Frequenzen' },
  ...Object.entries(freqLabels).map(([k, v]) => ({ value: k, label: v })),
]

// ─── Page ───────────────────────────────────────────────────────────────────

export function WiederkehrendePage() {
  const { toast } = useToast()
  const tenants = useTenantStore((s) => s.tenants)
  const fetchTenants = useTenantStore((s) => s.fetchTenants)

  const [selectedTenantId, setSelectedTenantId] = useState<string>('')
  const [months, setMonths] = useState(24)
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [syncStatus, setSyncStatus] = useState('')
  const [syncProgress, setSyncProgress] = useState({ done: 0, total: 0, rows: 0, cached: 0 })
  const [error, setError] = useState<string | null>(null)
  const [analysis, setAnalysis] = useState<RecurringAnalysis | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [directionFilter, setDirectionFilter] = useState<'all' | 'debit' | 'credit'>('debit')
  const [showInternalTransfers, setShowInternalTransfers] = useState(false)
  const [freqFilter, setFreqFilter] = useState<RecurrenceFrequency | 'all'>('all')
  const [savingPattern, setSavingPattern] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState<Set<string>>(new Set())

  const isJobRunning = syncing

  useEffect(() => { fetchTenants() }, [fetchTenants])
  useEffect(() => {
    if (!selectedTenantId && tenants.length > 0) setSelectedTenantId(tenants[0]!.id)
  }, [tenants, selectedTenantId])

  // Auto-Load: gespeichertes Ergebnis aus DB laden
  useEffect(() => {
    if (!selectedTenantId) return
    setLoading(true)
    apiClient.get('/buchhaltung/recurring/analyze', { params: { tenantId: selectedTenantId, months } })
      .then((res) => {
        const data = res.data as Record<string, unknown>
        if (data && Array.isArray(data.groups) && (data.groups as unknown[]).length > 0) {
          setAnalysis(data as unknown as RecurringAnalysis)
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [selectedTenantId, months])

  // Frontend-driven Sync: 3 Schritte, jeder ein eigener HTTP-Request
  const startRefresh = useCallback(async () => {
    if (!selectedTenantId || syncing) return
    setSyncing(true)
    setError(null)
    let totalRows = 0
    try {
      // Schritt 1: Headers laden, neue Auszuege identifizieren
      setSyncStatus('Lade Auszugs-Liste aus SAP...')
      const headers = await apiClient.post('/buchhaltung/recurring/sync-headers', {}, {
        params: { tenantId: selectedTenantId, months },
      })
      const { newAbsEntries, cached } = headers.data as { newAbsEntries: number[]; cached: { statements: number; rows: number } }
      setSyncProgress({ done: 0, total: newAbsEntries.length, rows: 0, cached: cached.statements })

      if (newAbsEntries.length === 0) {
        setSyncStatus('Keine neuen Auszüge — starte Erkennung...')
      } else {
        // Schritt 2: Jeden neuen Auszug einzeln laden + in DB speichern
        for (let i = 0; i < newAbsEntries.length; i += 1) {
          setSyncStatus(`Lade Auszug ${i + 1} von ${newAbsEntries.length}...`)
          setSyncProgress((p) => ({ ...p, done: i, total: newAbsEntries.length }))
          const result = await apiClient.post('/buchhaltung/recurring/sync-statement', {}, {
            params: { tenantId: selectedTenantId, absEntry: newAbsEntries[i] },
          })
          totalRows += (result.data as { rowCount: number }).rowCount ?? 0
          setSyncProgress((p) => ({ ...p, done: i + 1, rows: totalRows }))
        }
      }

      // Schritt 3: Detection
      setSyncStatus('Erkenne wiederkehrende Muster...')
      const detection = await apiClient.post('/buchhaltung/recurring/detect', {}, {
        params: { tenantId: selectedTenantId, months },
      })
      const result = detection.data as RecurringAnalysis
      if (result && Array.isArray(result.groups)) {
        setAnalysis(result)
      }
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { error?: string } }; message?: string }
      setError(err?.response?.data?.error || err?.message || 'Fehler beim Synchronisieren')
    } finally {
      setSyncing(false)
      setSyncStatus('')
    }
  }, [selectedTenantId, months, syncing])

  const handleSavePattern = async (group: RecurringGroup) => {
    if (!selectedTenantId) return
    setSavingPattern(group.groupKey)
    try {
      await apiClient.post('/buchhaltung/patterns', {
        tenantId: selectedTenantId,
        matchCounterparty: group.counterpartyNormalized || group.counterpartyName,
        matchPurpose: group.sampleDetails?.slice(0, 200) || undefined,
        proposedGlAccount: group.occurrences[0]?.glAccount || undefined,
        proposedBpCode: group.occurrences[0]?.bpCode || undefined,
        proposedDescription: `${freqLabels[group.frequency]}: ${group.counterpartyName}`,
      })
      setSaveSuccess((prev) => new Set(prev).add(group.groupKey))
      toast({ title: 'Buchungsmuster gespeichert', tone: 'success' })
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string }
      const msg = err?.response?.data?.error || err?.message || 'Fehler beim Speichern'
      toast({ title: 'Speichern fehlgeschlagen', description: msg, tone: 'danger' })
    } finally {
      setSavingPattern(null)
    }
  }

  // Filter + Default-Sortierung (nach Confidence absteigend).
  // Spalten-Sortierung uebernimmt DataTable selbst.
  const filteredGroups = useMemo(() => {
    if (!analysis) return []
    let groups = analysis.groups
    if (!showInternalTransfers) groups = groups.filter((g) => g.category !== 'internal_transfer')
    if (directionFilter !== 'all') groups = groups.filter((g) => g.direction === directionFilter)
    if (freqFilter !== 'all') groups = groups.filter((g) => g.frequency === freqFilter)
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      groups = groups.filter((g) =>
        g.counterpartyName.toLowerCase().includes(q) ||
        g.sampleDetails.toLowerCase().includes(q),
      )
    }
    return [...groups].sort((a, b) => b.confidence - a.confidence)
  }, [analysis, directionFilter, freqFilter, searchQuery, showInternalTransfers])

  // Summary stats — fokussiert auf Ausgaben (Vertraege/Fixkosten)
  const stats = useMemo(() => {
    if (!analysis) return null
    const expenseGroups = analysis.groups.filter((g) => g.category === 'expense')
    const incomeGroups = analysis.groups.filter((g) => g.category === 'income')
    const internalGroups = analysis.groups.filter((g) => g.category === 'internal_transfer')
    const totalExpenseAnnual = expenseGroups.reduce((s, g) => s + g.estimatedAnnualAmount, 0)
    return {
      totalGroups: analysis.groups.length,
      expenseGroups: expenseGroups.length,
      incomeGroups: incomeGroups.length,
      internalGroups: internalGroups.length,
      totalExpenseAnnual,
      statementsScanned: analysis.statementsScanned,
      rowsScanned: analysis.rowsScanned,
    }
  }, [analysis])

  const columns: ColumnDef<RecurringGroup, unknown>[] = useMemo(() => [
    {
      id: 'counterparty',
      accessorKey: 'counterpartyName',
      header: 'Empfänger / Absender',
      cell: ({ row }) => {
        const g = row.original
        return (
          <div className="flex items-center gap-1">
            <DirectionIcon direction={g.direction} />
            <span className="font-medium text-gray-900 truncate max-w-[250px]">
              {g.counterpartyName || '(unbekannt)'}
            </span>
          </div>
        )
      },
    },
    {
      id: 'details',
      accessorKey: 'sampleDetails',
      header: 'Verwendungszweck',
      enableSorting: false,
      cell: ({ row }) => {
        const s = row.original.sampleDetails
        return (
          <span className="text-gray-500 truncate max-w-[240px] block" title={s}>
            {s.slice(0, 60)}{s.length > 60 ? '...' : ''}
          </span>
        )
      },
    },
    {
      id: 'typicalAmount',
      accessorKey: 'typicalAmount',
      header: 'Betrag',
      size: 140,
      cell: ({ row }) => {
        const g = row.original
        return (
          <div className="text-center font-mono">
            <span className={g.direction === 'debit' ? 'text-red-600' : 'text-green-600'}>
              {g.direction === 'debit' ? '-' : '+'}{formatEur(g.typicalAmount)}
            </span>
            {g.amountStdev > 0.01 && (
              <span className="text-caption text-gray-400 block">
                {formatEur(g.amountMin)} – {formatEur(g.amountMax)}
              </span>
            )}
          </div>
        )
      },
    },
    {
      id: 'frequency',
      accessorKey: 'frequency',
      header: 'Frequenz',
      size: 140,
      cell: ({ row }) => (
        <div className="text-center">
          <Badge variant="info" size="sm">
            <CalendarClock className="w-3 h-3" />
            {freqLabels[row.original.frequency]}
          </Badge>
        </div>
      ),
    },
    {
      id: 'count',
      accessorKey: 'count',
      header: 'Anz.',
      size: 80,
      cell: ({ row }) => (
        <div className="text-center text-gray-700 font-medium">{row.original.count}</div>
      ),
    },
    {
      id: 'confidence',
      accessorKey: 'confidence',
      header: 'Score',
      size: 90,
      cell: ({ row }) => {
        const c = row.original.confidence
        return (
          <div className="text-center">
            <Badge variant={confidenceVariant(c)} size="sm">{c}%</Badge>
          </div>
        )
      },
    },
    {
      id: 'annual',
      accessorKey: 'estimatedAnnualAmount',
      header: 'p.a.',
      size: 130,
      cell: ({ row }) => (
        <div className="text-center font-mono text-gray-600">
          {formatEur(row.original.estimatedAnnualAmount)}
        </div>
      ),
    },
    {
      id: 'nextExpected',
      accessorKey: 'nextExpectedDate',
      header: 'Nächste',
      size: 110,
      cell: ({ row }) => (
        <div className="text-center text-caption text-gray-500">
          {row.original.nextExpectedDate ? formatDate(row.original.nextExpectedDate) : '–'}
        </div>
      ),
    },
    {
      id: 'action',
      header: 'Aktion',
      enableSorting: false,
      size: 130,
      cell: ({ row }) => {
        const g = row.original
        const isSaving = savingPattern === g.groupKey
        const isSaved = saveSuccess.has(g.groupKey)
        return (
          <div className="text-center" onClick={(e) => e.stopPropagation()}>
            {isSaved ? (
              <Badge variant="success" size="sm">Gespeichert</Badge>
            ) : (
              <Button
                size="xs"
                variant="secondary"
                leadingIcon={<Save />}
                loading={isSaving}
                onClick={() => handleSavePattern(g)}
                title="Buchungsmuster aus dieser Gruppe erstellen"
              >
                Muster
              </Button>
            )}
          </div>
        )
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [savingPattern, saveSuccess])

  const tenantOptions = useMemo(
    () => tenants.map((t) => ({ value: t.id, label: `${t.code} – ${t.name}` })),
    [tenants],
  )

  const pct = syncProgress.total > 0
    ? Math.round((syncProgress.done / syncProgress.total) * 100)
    : 0

  return (
    <div className="space-y-4 max-w-[1600px] mx-auto">
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Repeat className="w-6 h-6 text-brand-700" />
            Verträge &amp; Fixkosten
          </span>
        }
        description="Erkennung wiederkehrender Ausgaben aus Kontoauszügen — Lastschriften, Abonnements, laufende Verträge"
      />

      {/* Controls */}
      <Card>
        <Card.Body className="flex flex-wrap items-end gap-4">
          <Select
            label="Mandant"
            wrapperClassName="min-w-[220px]"
            value={selectedTenantId}
            onValueChange={(v) => { setSelectedTenantId(v); setAnalysis(null) }}
            options={tenantOptions}
            placeholder="Mandant wählen …"
          />
          <Select
            label="Zeitraum"
            wrapperClassName="min-w-[180px]"
            value={String(months)}
            onValueChange={(v) => { setMonths(parseInt(v)); setAnalysis(null) }}
            options={MONTHS_OPTIONS}
          />
          <Button
            leadingIcon={<RefreshCw />}
            loading={isJobRunning}
            disabled={isJobRunning || !selectedTenantId}
            onClick={startRefresh}
          >
            {isJobRunning ? 'Läuft...' : 'Daten aktualisieren'}
          </Button>
        </Card.Body>
      </Card>

      {/* Progress */}
      {syncing && (
        <Card className="border-brand-200">
          <Card.Body className="space-y-2">
            <div className="flex items-center gap-3">
              <Spinner size="md" />
              <div className="flex-1">
                <p className="text-body font-medium text-brand-700">{syncStatus}</p>
                {syncProgress.total > 0 && (
                  <p className="text-caption text-gray-500 mt-0.5">
                    {syncProgress.done} von {syncProgress.total} neuen Auszügen
                    {syncProgress.cached > 0 && <> · {syncProgress.cached} bereits gespeichert</>}
                    {syncProgress.rows > 0 && <> · {syncProgress.rows} Zeilen</>}
                  </p>
                )}
              </div>
              {syncProgress.total > 0 && (
                <span className="text-body font-bold text-brand-700">{pct}%</span>
              )}
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
              <div
                className={syncProgress.total > 0
                  ? 'bg-brand-500 h-2 rounded-full transition-all duration-300'
                  : 'bg-brand-400 h-2 rounded-full w-1/3 animate-pulse'}
                style={syncProgress.total > 0 ? { width: `${pct}%` } : undefined}
              />
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Error */}
      {error && (
        <Card>
          <Card.Body>
            <ErrorState
              title="Fehler beim Synchronisieren"
              description={error}
              details={error}
              onRetry={startRefresh}
              size="sm"
            />
          </Card.Body>
        </Card>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          <StatCard label="Laufende Verträge" value={String(stats.expenseGroups)} icon={<Repeat className="w-5 h-5 text-brand-500" />} />
          <StatCard label="Fixkosten p.a." value={formatEur(stats.totalExpenseAnnual)} sub="hochgerechnet" icon={<TrendingDown className="w-5 h-5 text-red-500" />} />
          <StatCard label="Analysiert" value={`${stats.rowsScanned} Zeilen`} sub={`${stats.statementsScanned} Auszüge`} />
          <StatCard label="Wied. Eingänge" value={String(stats.incomeGroups)} sub="(Miete, Rückflüsse etc.)" />
          {stats.internalGroups > 0 && (
            <StatCard label="Interne Überträge" value={String(stats.internalGroups)} sub="ausgeblendet" />
          )}
        </div>
      )}

      {/* Warnings */}
      {analysis && analysis.errors.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <Card.Body>
            <p className="text-small font-medium text-amber-700">
              {analysis.errors.length} Warnung(en) beim Laden:
            </p>
            <ul className="text-caption text-amber-700 mt-1 space-y-0.5">
              {analysis.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
              {analysis.errors.length > 5 && <li>... und {analysis.errors.length - 5} weitere</li>}
            </ul>
          </Card.Body>
        </Card>
      )}

      {/* Debug-Info */}
      {analysis?.debug && (
        <DebugPanel
          debug={analysis.debug}
          statementsScanned={analysis.statementsScanned}
          windowFrom={analysis.windowFromDate}
          windowTo={analysis.windowToDate}
        />
      )}

      {/* Filter bar */}
      {analysis && analysis.groups.length > 0 && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              wrapperClassName="flex-1 min-w-[220px]"
              leadingIcon={<Search />}
              placeholder="Empfänger oder Verwendungszweck suchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
            />
            <Select
              wrapperClassName="min-w-[190px]"
              value={freqFilter}
              onValueChange={(v) => setFreqFilter(v as typeof freqFilter)}
              options={FREQ_OPTIONS}
            />
          </div>
          <FilterBar>
            <FilterChip
              active={directionFilter === 'debit'}
              onClick={() => setDirectionFilter('debit')}
              leadingIcon={<TrendingDown />}
            >
              Nur Ausgaben
            </FilterChip>
            <FilterChip
              active={directionFilter === 'credit'}
              onClick={() => setDirectionFilter('credit')}
              leadingIcon={<TrendingUp />}
            >
              Nur Eingänge
            </FilterChip>
            <FilterChip
              active={directionFilter === 'all'}
              onClick={() => setDirectionFilter('all')}
            >
              Alles
            </FilterChip>
            <div className="ml-auto flex items-center gap-3">
              <Checkbox
                checked={showInternalTransfers}
                onChange={(e) => setShowInternalTransfers(e.currentTarget.checked)}
                label={<span className="text-small text-gray-600">Interne Überträge</span>}
              />
              <span className="text-caption text-gray-400">
                {filteredGroups.length} / {analysis.groups.length}
              </span>
            </div>
          </FilterBar>
        </>
      )}

      {/* Results table */}
      {analysis && filteredGroups.length > 0 && (
        <DataTable<RecurringGroup>
          data={filteredGroups}
          columns={columns}
          getRowId={(g) => g.groupKey}
          getRowCanExpand={() => true}
          renderExpandedRow={(row: Row<RecurringGroup>) => <OccurrencesDetail group={row.original} />}
        />
      )}

      {/* States: Loading / Empty */}
      {loading && !analysis && !isJobRunning && (
        <LoadingState label="Lade gespeicherte Analyse..." />
      )}
      {!loading && !analysis && !isJobRunning && (
        <EmptyState
          icon={<BarChart3 />}
          title="Noch keine Analyse vorhanden"
          description={'Klicke „Daten aktualisieren" um die Kontoauszüge erstmalig zu analysieren.'}
          size="lg"
        />
      )}
      {analysis && filteredGroups.length === 0 && (
        <EmptyState
          icon={<CalendarClock />}
          title="Keine laufenden Verträge erkannt"
          description={'Versuche einen längeren Zeitraum, anderen Filter oder klicke „Daten aktualisieren".'}
          size="lg"
        />
      )}

      {/* Letztes Update */}
      {analysis && !isJobRunning && (
        <p className="text-caption text-gray-400 text-center">
          Letzte Aktualisierung: {new Date(analysis.generatedAt).toLocaleString('de-DE')} –{' '}
          <Button variant="link" size="xs" onClick={startRefresh} disabled={isJobRunning}>
            Jetzt aktualisieren
          </Button>
        </p>
      )}

      {/* DB Debug */}
      <DbDebug />
    </div>
  )
}

// ─── Sub-Components ─────────────────────────────────────────────────────────

function StatCard({ label, value, sub, icon }: { label: string; value: string; sub?: string; icon?: React.ReactNode }) {
  return (
    <Card>
      <Card.Body className="p-3">
        <div className="flex items-center gap-2 mb-1">
          {icon}
          <span className="text-caption font-medium text-gray-500">{label}</span>
        </div>
        <p className="text-h4 text-gray-900">{value}</p>
        {sub && <p className="text-caption text-gray-400">{sub}</p>}
      </Card.Body>
    </Card>
  )
}

function OccurrencesDetail({ group: g }: { group: RecurringGroup }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-4 text-caption text-gray-500 flex-wrap">
        <span>Erste Buchung: {formatDate(g.firstDate)}</span>
        <span>Letzte Buchung: {formatDate(g.lastDate)}</span>
        <span>Median-Intervall: {g.medianIntervalDays} Tage (Stdev: {g.intervalStdevDays})</span>
        {g.unbookedCount > 0 && (
          <Badge variant="warning" size="sm">{g.unbookedCount} ungebucht</Badge>
        )}
      </div>
      <DataTable
        data={g.occurrences}
        getRowId={(occ, i) => `${occ.statementAbsEntry}-${occ.lineNum}-${i}`}
        enableSorting={false}
        columns={[
          {
            accessorKey: 'postingDate',
            header: 'Datum',
            cell: ({ row }) => formatDate(row.original.postingDate),
          },
          {
            accessorKey: 'counterpartyName',
            header: 'Empfänger',
            cell: ({ row }) => (
              <span className="truncate max-w-[180px] block">{row.original.counterpartyName}</span>
            ),
          },
          {
            accessorKey: 'details',
            header: 'Verwendungszweck',
            cell: ({ row }) => (
              <span className="text-gray-500 truncate max-w-[250px] block" title={row.original.details}>
                {row.original.details}
              </span>
            ),
          },
          {
            accessorKey: 'amount',
            header: 'Betrag',
            cell: ({ row }) => (
              <span className={`block text-right font-mono ${row.original.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatEur(Math.abs(row.original.amount))}
              </span>
            ),
          },
          {
            accessorKey: 'bpCode',
            header: 'GP-Code',
            cell: ({ row }) => (
              <span className="block text-center text-gray-500">{row.original.bpCode || '–'}</span>
            ),
          },
          {
            accessorKey: 'glAccount',
            header: 'Sachkonto',
            cell: ({ row }) => (
              <span className="block text-center text-gray-500">{row.original.glAccount || '–'}</span>
            ),
          },
          {
            accessorKey: 'isBooked',
            header: 'Status',
            cell: ({ row }) => (
              <div className="flex justify-center">
                {row.original.isBooked
                  ? <Badge variant="success" size="sm">Gebucht</Badge>
                  : <Badge variant="neutral" size="sm">Offen</Badge>}
              </div>
            ),
          },
        ]}
      />
    </div>
  )
}

function DebugPanel({ debug, statementsScanned, windowFrom, windowTo }: {
  debug: AnalysisDebug
  statementsScanned: number
  windowFrom: string
  windowTo: string
}) {
  const [open, setOpen] = useState(false)
  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-2 text-caption text-gray-500 hover:text-gray-700"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Analyse-Details (Debug)
      </button>
      {open && (
        <div className="px-4 pb-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-1 text-caption">
          <DebugRow label="Zeitfenster" value={`${formatDate(windowFrom)} – ${formatDate(windowTo)}`} />
          <DebugRow label="Auszüge (Statements)" value={String(statementsScanned)} />
          <DebugRow label="Zeilen gesamt (roh)" value={String(debug.totalRawRows)} />
          <DebugRow label="Verworfen: kein Datum" value={String(debug.droppedNoDate)} warn={debug.droppedNoDate > 0} />
          <DebugRow label="Verworfen: außerhalb Zeitraum" value={String(debug.droppedDateOutOfRange)} />
          <DebugRow label="Verworfen: Betrag = 0" value={String(debug.droppedZeroAmount)} />
          <DebugRow label="Zeilen nach Filter" value={String(debug.rowsAfterFilter)} highlight />
          <DebugRow label="Davon mit Empfänger" value={String(debug.rowsWithCounterparty)} />
          <DebugRow label="Davon ohne Empfänger" value={String(debug.rowsWithoutCounterparty)} warn={debug.rowsWithoutCounterparty > 10} />
          <DebugRow label="Gruppierungs-Buckets" value={String(debug.bucketsFormed)} />
          <DebugRow label="Buckets mit ≥2 Vorkommen" value={String(debug.bucketsWithEnoughOccurrences)} />
          <DebugRow label="Gruppen vor Score-Filter" value={String(debug.groupsBeforeConfidenceFilter)} />
          <DebugRow label="Gruppen final" value={String(debug.groupsAfterConfidenceFilter)} highlight />
        </div>
      )}
    </Card>
  )
}

function DebugRow({ label, value, warn, highlight }: { label: string; value: string; warn?: boolean; highlight?: boolean }) {
  return (
    <div className="flex justify-between py-0.5">
      <span className="text-gray-500">{label}</span>
      <span className={`font-mono ${warn ? 'text-amber-700 font-bold' : highlight ? 'text-brand-700 font-bold' : 'text-gray-900'}`}>{value}</span>
    </div>
  )
}

function DbDebug() {
  const [info, setInfo] = useState<Record<string, unknown> | null>(null)
  const [open, setOpen] = useState(false)
  const load = () => {
    apiClient.get('/buchhaltung/recurring/debug').then((r) => setInfo(r.data as Record<string, unknown>)).catch(() => setInfo({ error: 'Endpoint nicht erreichbar' }))
  }
  return (
    <div className="text-center mt-2">
      <button
        type="button"
        onClick={() => { setOpen(!open); if (!open) load() }}
        className="text-caption text-gray-300 hover:text-gray-500 underline"
      >
        DB-Status
      </button>
      {open && info && (
        <pre className="text-caption text-left bg-gray-50 border border-gray-200 rounded p-2 mt-1 mx-auto max-w-xl overflow-auto">
          {JSON.stringify(info, null, 2)}
        </pre>
      )}
    </div>
  )
}
